import React from 'react';
import { Container, TabContent, TabPane } from 'reactstrap';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import Breadcrumb from 'components/App/Breadcrumb';
import Meta from 'components/App/Meta';
import StepBasic from 'components/AddLocation/StepBasic';
import StepPolicy from 'components/AddLocation/StepPolicy';
import StepAddImages from 'components/AddLocation/StepAddImages';
import StepRoomInformation from 'components/AddLocation/StepRoomInformation';
import StepAmenitiesService from 'components/AddLocation/StepAmenitiesService';
import StepPayment from 'components/AddLocation/StepPayment';
import Selection from 'components/App/Selection';

import images from 'assets/images';
import { ReactComponent as UserIconSVG } from 'assets/images/location/person-pin.svg';
import { ReactComponent as TagIconSVG } from 'assets/images/location/local-offer.svg';
import { ReactComponent as ServicesIconSVG } from 'assets/images/location/restaurant-menu.svg';
import { ReactComponent as ImageIconSVG } from 'assets/images/location/add-photo_alternate.svg';
import { ReactComponent as PolicyIconSVG } from 'assets/images/location/event-note.svg';
import { ReactComponent as MoneyIconSVG } from 'assets/images/location/bx-money.svg';
import { LOCATIONS } from 'constants/index';
import TAB from 'constants/tab';
import INITIAL_VALUE from 'constants/initialValue';
import propTypeModels from 'propTypeModels';

import Button from 'components/App/Button';
import styles from './EditLocation.module.scss';

const NAV_TAB = [
  {
    icon: UserIconSVG,
    title: 'Thông tin cơ bản',
    value: TAB.BASIC,
  },
  {
    icon: TagIconSVG,
    title: 'Phòng và Giá',
    value: TAB.ROOM_PRICE,
  },
  {
    icon: ServicesIconSVG,
    title: 'Tiện nghi và dịch vụ',
    value: TAB.AMENITIES_SERVICES,
  },
  {
    icon: ImageIconSVG,
    title: 'Hình ảnh',
    value: TAB.ADD_IMAGES,
  },
  {
    icon: PolicyIconSVG,
    title: 'Chính sách',
    value: TAB.POLICY,
  },
  {
    icon: MoneyIconSVG,
    title: 'Thanh toán',
    value: TAB.PAYMENT,
  },
];

const breadcrumbList = [
  {
    name: 'Quản lý chỗ nghỉ',
    isActive: false,
    link: LOCATIONS.LOCATION_LIST,
  },
  {
    name: 'Thêm chỗ nghỉ',
    isActive: true,
    link: '',
  },
];

const EditLocation = (props) => {
  const {
    isLoadingCreateNewPlace,
    loadingUploadContract,

    districts,
    me,
    provinces,
    setting,
    step,
    stepAvailable,
    wards,
    location,

    actionCreateNewPlace,
    actionOpenModalCreatePlaceSuccess,
    actionAddLocationByStep,
    actionGetProvinceList,
    actionGetDistrictList,
    actionGetWardList,
    actionChangeTab,
    actionUploadContract,
  } = props;

  const topRef = React.useRef();
  const [isValid, setIsValid] = React.useState(true);
  const [status, setStatus] = React.useState(location?.status || '');
  const [showStatusError, setShowStatusError] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const statusOptions = React.useMemo(() => [
    { label: 'Liên hệ', value: 'CONTACTING' },
    { label: 'Ký hợp đồng', value: 'ASSIGNED' },
    { label: 'Sẵn sàng', value: 'READY' },
    { label: 'Đổi chủ', value: 'CHANGED_OWNER' },
  ], []);

  const onChangeStatus = React.useCallback((selected) => {
    const value = selected?.value || '';
    setStatus(value);
    setShowStatusError(false);
    actionAddLocationByStep({
      step,
      location: {
        ...location,
        status: value,
      },
    });
  }, [actionAddLocationByStep, location, step]);

  React.useEffect(() => {
    if (location?.status) {
      setStatus(location.status);
    }
  }, [location?.status]);

  const handleCreateNewPlace = React.useCallback((payload) => {
    if (!status) {
      setShowStatusError(true);
      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }
    actionCreateNewPlace(payload);
  }, [actionCreateNewPlace, status]);

  const onUploadClick = React.useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const onFileChange = React.useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      actionUploadContract({
        file,
        callback: (url) => {
          actionAddLocationByStep({
            step,
            location: {
              ...location,
              contract: url,
            },
          });
        },
      });
    }
  }, [actionUploadContract, actionAddLocationByStep, location, step]);

  const onDeleteContract = React.useCallback(() => {
    actionAddLocationByStep({
      step,
      location: {
        ...location,
        contract: '',
      },
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [actionAddLocationByStep, location, step]);

  const onChangeTab = React.useCallback(
    (value) => () => {
      if (stepAvailable.includes(value) && isValid) {
        actionChangeTab({
          step: value,
        });
      }
    },
    [actionChangeTab, isValid, stepAvailable],
  );

  const handleAddLocationByStep = React.useCallback((payload) => {
    actionAddLocationByStep(payload);
  }, [actionAddLocationByStep]);

  const onBack = React.useCallback(
    (value) => () => {
      actionChangeTab({
        step: value,
      });
    },
    [actionChangeTab],
  );

  React.useEffect(() => {
    document
      .getElementsByClassName('main-content')[0]
      .scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  return (
    <>
      <div className="page-content" ref={topRef}>
        <Meta name="Thêm chỗ nghỉ" />

        <Container fluid>
          {step === TAB.FINAL_STEP ? (
            <div className={styles.PageFinal}>
              <div className="Modal">
                <h3 className="Modal__Title">
                  Chúng tôi đang tạo tài khoản Chỗ nghỉ cho Quý vị
                </h3>

                <div className="Modal__Description">
                  <p>
                    Hãy thư giãn nhé vì bây giờ là lúc chúng tôi thiết lập tài
                    khoản chỗ nghỉ cho Quý vị và chuẩn bị sẵn sàng tài khoản
                    Connect (extranet).
                  </p>

                  <p>
                    Nhằm giúp Quý vị lưu giữ hồ sơ, chúng tôi cũng đã gửi cho
                    Quý vị một email xác nhận đăng ký cùng với một email khác có
                    đính kèm bản sao thỏa thuận của Quý vị.
                  </p>

                  <p>
                    Chúng tôi sẽ gửi email cho Quý vị ngay khi mọi việc đã sẵn
                    sàng.
                  </p>
                </div>

                <img src={images.finalStepImage.default} alt="final step" />
              </div>
            </div>
          ) : (
            <>
              <Breadcrumb
                pageTitle="Thêm chỗ nghỉ"
                breadcrumbList={breadcrumbList}
              />

              <div className={styles.Page}>
                <ul className={classnames(styles.NavMenu, 'custom-scrollbar')}>
                  {NAV_TAB.map(({ icon: Icon, title, value }) => (
                    <li key={value}>
                      <Button
                        isDefault
                        classes={classnames(
                          styles.NavMenuItem,
                          stepAvailable.includes(value) &&
                          step !== value &&
                          styles.NavMenuItemDone,
                          step === value && styles.NavMenuItemActive,
                        )}
                        onClick={onChangeTab(value)}
                      >
                        <Icon />

                        <p>{title}</p>
                      </Button>
                    </li>
                  ))}
                </ul>

                <section className={styles.NavBody}>
                  <TabContent activeTab={step}>
                    <TabPane tabId={TAB.BASIC}>
                      <StepBasic
                        username={me.username || me.name}
                        actionGetProvinceList={actionGetProvinceList}
                        provinces={provinces}
                        actionGetDistrictList={actionGetDistrictList}
                        districts={districts}
                        actionGetWardList={actionGetWardList}
                        wards={wards}
                        actionAddLocationByStep={handleAddLocationByStep}
                        location={location}
                        setIsValid={setIsValid}
                      />
                    </TabPane>

                    <TabPane tabId={TAB.ROOM_PRICE}>
                      <StepRoomInformation
                        actionAddLocationByStep={handleAddLocationByStep}
                        onBack={onBack}
                        location={location}
                        setIsValid={setIsValid}
                      />
                    </TabPane>

                    <TabPane tabId={TAB.AMENITIES_SERVICES}>
                      <StepAmenitiesService
                        setting={setting}
                        actionAddLocationByStep={handleAddLocationByStep}
                        onBack={onBack}
                        location={location}
                        setIsValid={setIsValid}
                      />
                    </TabPane>

                    <TabPane tabId={TAB.ADD_IMAGES}>
                      <StepAddImages
                        actionAddLocationByStep={handleAddLocationByStep}
                        onBack={onBack}
                        setIsValid={setIsValid}
                      />
                    </TabPane>

                    <TabPane tabId={TAB.POLICY}>
                      <StepPolicy
                        setting={setting}
                        actionAddLocationByStep={handleAddLocationByStep}
                        onBack={onBack}
                        location={location}
                        setIsValid={setIsValid}
                      />
                    </TabPane>

                    <TabPane tabId={TAB.PAYMENT}>
                      <StepPayment
                        isLoadingCreateNewPlace={isLoadingCreateNewPlace}
                        location={location}
                        actionOpenModalCreatePlaceSuccess={
                          actionOpenModalCreatePlaceSuccess
                        }
                        actionCreateNewPlace={handleCreateNewPlace}
                        actionAddLocationByStep={handleAddLocationByStep}
                        onBack={onBack}
                        setIsValid={setIsValid}
                        setting={setting}
                      />
                    </TabPane>
                  </TabContent>
                </section>

                <div className={styles.NavInformation}>
                  <section className={styles.NavInformationCard}>
                    <label className={styles.Label}>
                      Trạng thái chỗ nghỉ
                      <span className="text-danger">
                        *
                      </span>
                    </label>
                    <Selection
                      selectedClassName={classnames(
                        styles.StatusSelect,
                        showStatusError && styles.StatusSelectError,
                      )}
                      placeholder="Chọn trạng thái"
                      options={statusOptions}
                      value={status}
                      onChange={onChangeStatus}
                    />
                    {showStatusError && (
                      <span className="text-danger mt-1 font-size-12">
                        Trạng thái chỗ nghỉ là bắt buộc
                      </span>
                    )}
                  </section>

                  <section className={styles.NavInformationCard}>
                    <label className={styles.Label}>Hợp đồng</label>
                    {location?.contract ? (
                      <div className={styles.FileUploadedCard}>
                        <div className={styles.FileInfo}>
                          <i className="bx bxs-file-pdf text-danger font-size-24" />
                          <div className={styles.FileText}>
                            <a
                              href={location.contract}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.FileName}
                              title={location.contract.split('/').pop()}
                            >
                              {location.contract.split('/').pop()}
                            </a>
                            <span className={styles.FileSuccessText}>Tải lên thành công</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className={styles.DeleteFileButton}
                          onClick={onDeleteContract}
                          aria-label="Xóa hợp đồng"
                        >
                          <i className="bx bx-trash" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={styles.UploadButton}
                          onClick={onUploadClick}
                          disabled={loadingUploadContract}
                        >
                          {loadingUploadContract ? (
                            <>
                              <i className="bx bx-loader bx-spin font-size-18 align-middle" />
                              Đang tải lên...
                            </>
                          ) : (
                            <>
                              <i className="bx bx-upload" />
                              Tải lên
                            </>
                          )}
                        </button>
                        <input
                          type="file"
                          ref={fileInputRef}
                          style={{ display: 'none' }}
                          accept="application/pdf,.pdf"
                          onChange={onFileChange}
                        />
                      </>
                    )}
                  </section>
                </div>
              </div>
            </>
          )}
        </Container>
      </div>
    </>
  );
};

EditLocation.propTypes = {
  isLoadingCreateNewPlace: PropTypes.bool.isRequired,

  me: propTypeModels.user.userPropTypes.isRequired,

  step: PropTypes.string.isRequired,
  setting: PropTypes.instanceOf(Object),
  provinces: PropTypes.instanceOf(Array),
  districts: PropTypes.instanceOf(Array),
  wards: PropTypes.instanceOf(Array),
  stepAvailable: PropTypes.instanceOf(Array),
  location: PropTypes.instanceOf(Object),

  actionCreateNewPlace: PropTypes.func.isRequired,
  actionOpenModalCreatePlaceSuccess: PropTypes.func.isRequired,
  actionAddLocationByStep: PropTypes.func.isRequired,
  actionGetProvinceList: PropTypes.func.isRequired,
  actionGetDistrictList: PropTypes.func.isRequired,
  actionGetWardList: PropTypes.func.isRequired,
  actionChangeTab: PropTypes.func.isRequired,
  actionUploadContract: PropTypes.func.isRequired,
  loadingUploadContract: PropTypes.bool,
};

EditLocation.defaultProps = {
  setting: INITIAL_VALUE.OBJECT,
  provinces: INITIAL_VALUE.ARRAY,
  districts: INITIAL_VALUE.ARRAY,
  wards: INITIAL_VALUE.ARRAY,
  stepAvailable: INITIAL_VALUE.ARRAY,
  location: INITIAL_VALUE.OBJECT,
  loadingUploadContract: false,
};

export default EditLocation;
